import React, { useState } from 'react';
import type { LogEntry } from '../hooks/useLogs';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChevronDown, ChevronUp } from 'lucide-react';

const ACTION_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  login: { label: 'CONNEXION', color: '#2563EB', bg: 'rgba(37,99,235,0.1)' },
  update_match: { label: 'MODIF. MATCH', color: '#C67A00', bg: 'rgba(198,122,0,0.1)' },
  add_match: { label: 'AJOUT MATCH', color: '#2D6A2D', bg: 'rgba(45,106,45,0.1)' },
  add_bar: { label: 'AJOUT BAR', color: '#2D6A2D', bg: 'rgba(45,106,45,0.1)' },
  confirm_diffusion: { label: 'DIFFUSION', color: '#1B7A1B', bg: 'rgba(27,122,27,0.1)' },
  delete_match: { label: 'SUPPRESSION', color: '#C53030', bg: 'rgba(197,48,48,0.1)' },
  delete_bar: { label: 'SUPPR. BAR', color: '#C53030', bg: 'rgba(197,48,48,0.1)' },
  update_bar: { label: 'MODIF. BAR', color: '#7C3AED', bg: 'rgba(124,58,237,0.1)' },
  invite_admin: { label: 'INVITATION', color: '#2563EB', bg: 'rgba(37,99,235,0.1)' },
  remove_admin: { label: 'RETRAIT ADMIN', color: '#C53030', bg: 'rgba(197,48,48,0.1)' },
};

interface Props {
  log: LogEntry;
}

export function LogRow({ log }: Props) {
  const [expanded, setExpanded] = useState(false);
  const config = ACTION_CONFIG[log.action] || ACTION_CONFIG.login;
  const hasDetails = log.old_value || log.new_value;
  const dateStr = format(new Date(log.created_at), 'dd/MM · HH\'h\'mm', { locale: fr });
  const adminName = log.admin_email.split('@')[0];

  return (
    <div className="log-card">
      <div className="log-card-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
          <span style={{
            padding: '3px 10px',
            borderRadius: '20px',
            fontSize: '10px',
            fontWeight: 700,
            color: config.color,
            background: config.bg,
            letterSpacing: '0.5px',
          }}>
            {config.label}
          </span>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{dateStr}</span>
        </div>
        <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>{adminName}</span>
      </div>
      <p style={{ fontSize: '13px', color: 'var(--text)', margin: '6px 0 0' }}>
        {log.description || '-'}
      </p>
      {hasDetails && (
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            marginTop: '8px',
            background: 'none',
            border: 'none',
            color: 'var(--accent)',
            fontSize: '11px',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {expanded ? 'Masquer' : 'Voir détails'}
        </button>
      )}
      {expanded && hasDetails && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '10px' }}>
          <div>
            <div style={{ fontSize: '10px', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '4px' }}>Avant</div>
            <pre style={{
              background: 'var(--bg-primary)',
              padding: '10px',
              borderRadius: '6px',
              fontSize: '10px',
              color: 'var(--orange)',
              overflow: 'auto',
              maxHeight: '200px',
            }}>
              {JSON.stringify(log.old_value, null, 2) || 'null'}
            </pre>
          </div>
          <div>
            <div style={{ fontSize: '10px', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '4px' }}>Après</div>
            <pre style={{
              background: 'var(--bg-primary)',
              padding: '10px',
              borderRadius: '6px',
              fontSize: '10px',
              color: 'var(--green)',
              overflow: 'auto',
              maxHeight: '200px',
            }}>
              {JSON.stringify(log.new_value, null, 2) || 'null'}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
